import {
  base64ToCursor,
  compareCursor,
  cursorToBase64,
  isCanonicalSequenceCursor,
} from '../nats/sequence-id.js';

export function applyLiveEventSchedule<
  T extends {
    readonly eventId: string;
    readonly sequenceId: string;
    readonly tradingStartAt: string;
    readonly tradingEndAt: string;
  },
>(row: T, raw: Record<string, unknown>): T {
  if (raw.scheduledEvent !== row.eventId || !isCanonicalSequenceCursor(raw.sequenceId)) return row;
  const previous = base64ToCursor(row.sequenceId);
  if (previous !== null && compareCursor(raw.sequenceId, previous) <= 0) return row;
  const start = timestamp(raw.tradingOpenedTs);
  const end = timestamp(raw.tradingClosedTs);
  const sequenceId = cursorToBase64(raw.sequenceId);
  if (start === null || end === null || start >= end || sequenceId === null) return row;
  return { ...row, tradingStartAt: start, tradingEndAt: end, sequenceId };
}

function timestamp(value: unknown): string | null {
  if (typeof value !== 'bigint' && typeof value !== 'string' && typeof value !== 'number')
    return null;
  const milliseconds = Number(value) * 1000;
  if (!Number.isSafeInteger(milliseconds)) return null;
  const date = new Date(milliseconds);
  return Number.isNaN(date.getTime()) ? null : date.toISOString();
}

// REST may lag an already applied indexed schedule, including after reconnect.
// Preserve only the schedule; newer editorial fields still come from REST.
export function preserveLiveEventSchedule(previous: unknown, incoming: object): object {
  if (previous === null || typeof previous !== 'object') return incoming;
  const before = previous as Record<string, unknown>;
  const after = incoming as Record<string, unknown>;
  if (typeof before.sequenceId !== 'string' || typeof after.sequenceId !== 'string')
    return incoming;
  const oldSequence = base64ToCursor(before.sequenceId);
  const newSequence = base64ToCursor(after.sequenceId);
  if (oldSequence === null || newSequence === null || compareCursor(newSequence, oldSequence) >= 0)
    return incoming;
  return {
    ...incoming,
    sequenceId: before.sequenceId,
    tradingStartAt: before.tradingStartAt,
    tradingEndAt: before.tradingEndAt,
  };
}
